











import Vue from 'vue';
import { defineComponent, reactive, inject, watchEffect, computed } from '@vue/composition-api';
import { WokDataGridConfiguration, WokDataGridColumn, formatCellValue, getCellValue, explodeRowProps } from './helpers';

export default defineComponent({
    name: 'WokDataGridCell',

    props: {
        data:  {
            default: ''
        },
        dataName:  {
            type: String,
            default: undefined
        },
        dataIndex:  {
            type: [String, Number],
            default: undefined
        },
        dataType:  {
            type: String,
            default: 'text',
            validator: (v: string) => v == '' || /(text|integer|number|currency|date)/.test(v)
        },
        dataPrecision:  {
            type: Number,
            // The default here is OK for currency too.
            default: 2
        },
        dataAggregate: {
            type: Function,
            default: undefined
        },
        label: {
            type: String,
            default: ''
        },
        fmt: {
            type: Function,
            default: null
        },
        align: {
            type: String,
            default: '',
            validator: (v: string) => v == '' || /(left|center|right)/.test(v)
        },
        display: {
            type: String,
            default: 'show',
            validator: (v: string) => v == '' || /(show|hide|report|ui)/.test(v)
        },
        selected: {
            type: [String, Boolean],
            default: false
        },
        colspan: {
            type: [String, Number],
            default: undefined
        },
        columnWidth: {
            type: [String, Number],
            default: undefined
        },
        headerSpan: {
            type: [String, Number],
            default: 1
        },
        textColor: {
            type: String,
            default: undefined
        }
    },

    setup(props, context) {
        // Inject table configuration and other data needed by this cell.
        const cfg = inject<WokDataGridConfiguration>('wokDataGridConfiguration');

        const format = (v: any, i: number) =>
            formatCellValue(item.value, v, i, props.dataType, props.dataPrecision, cfg?.locale, props.fmt)

        const { item, row, index } = explodeRowProps(inject('wokDataGridRowProps'));

        const cellValue = computed(() => getCellValue(item.value, props.data, props.dataName, props.dataIndex));

        const cellNameIndex = computed(() => props.dataIndex != null ? `${props.dataName}[${props.dataIndex}]` : props.dataName);

        const formattedValue = computed(() => format(cellValue.value, index.value))

        // Formatting functions and default alignments.

        const align = computed(() => {
            if (props.align) {
                return props.align;
            }
            switch (props.dataType) {
                case 'integer':
                case 'number':
                case 'currency':
                    return 'right';
                default:
                    return 'left';
            }
        })

        // The first cell of every column is responsible for updating the
        // global table configuration.

        if (cfg && row.value == 1) {
            if (cfg.update) {
                cfg.update = false;
                cfg.columns = [];
            }

            const cell = reactive<WokDataGridColumn>({
                index: cfg.columns.length,
                label: '',
                align: '',
                display: '',
                headerSpan: 1,
                dataName: null,
                dataIndex: null,
                dataType: '',
                dataPrecision: 0,
                dataFormat: null,
                dataAggregate: null
            });

            cfg.columns.push(cell);

            watchEffect(() => {
                cell.label = props.label;
                cell.align = align.value;
                cell.display = props.display;
                cell.headerSpan = typeof props.headerSpan == 'string' ? parseInt(props.headerSpan) : props.headerSpan,
                cell.dataType = props.dataType;
                cell.dataName = props.dataName ?? null;
                cell.dataIndex = props.dataIndex ?? null;
                cell.dataPrecision = props.dataPrecision;
                cell.dataFormat = props.fmt || null;
                cell.dataAggregate = props.dataAggregate || null;
            });
        }

        const cellClasses = computed(() => {
                /* Expected classes:
                    wok--w-1/24
                    wok--w-2/24
                    wok--w-3/24
                    wok--w-4/24
                    wok--w-5/24
                    wok--w-6/24
                    wok--w-7/24
                    wok--w-8/24
                    wok--w-9/24
                    wok--w-10/24
                    wok--w-11/24
                    wok--w-12/24
                    wok--w-13/24
                    wok--w-14/24
                    wok--w-15/24
                    wok--w-16/24
                    wok--w-17/24
                    wok--w-18/24
                    wok--w-19/24
                    wok--w-20/24
                    wok--w-21/24
                    wok--w-22/24
                    wok--w-23/24
                    wok--text-right
                    wok--text-left
                    wok--text-center
                */

            // If given convert the column width to fraction.
            let colWidth = props.columnWidth?.toString();
            if (colWidth) {
                if (/^([123456789]|1\d|20|21|22|23)$/.test(colWidth)) {
                    colWidth = `wok--w-${colWidth}/24`;
                }
                else {
                    colWidth = 'wok--w-full';
                }
            }

            return {
                'wok--data-grid__table-cell': true,
                'wok--data-grid__table-cell-selected': props.selected,
                [`wok--text-${align.value}`]: true,
                [`wok--text-${props.textColor}`]: props.textColor,
                [colWidth]: !!colWidth,
            }
        });

        return {
            item,
            index,
            row,
            format,
            formattedValue,
            cellClasses,
            cellValue,
            cellNameIndex
        }
    }
})
